import {
  AUTOMATION_EVENT_OPTIONS,
  createDefaultAutomation as createTrackerAutomationSettings,
  getTrackerById,
  getTrackers,
  normalizeAutomationEvents,
  saveTracker,
} from "../trackerStorage";
import { getQuestions } from "../questions/questionStorage";
import {
  addTrackerAutomationApi,
  deleteTrackerAutomationApi,
  getTrackerAutomationDetailApi,
  getTrackerAutomationsApi,
  updateTrackerAutomationApi,
} from "../../../DAL/trackerAutomation/trackerAutomation";

const STORAGE_KEY = "gymrite_tracker_automations_v2";
const LEGACY_STORAGE_KEY = "gymrite_tracker_automations";

/** Trigger models from GymRite docs — events-first, not Power Automate–locked. */
export const AUTOMATION_TRIGGER_MODELS = [
  {
    value: "event",
    title: "Event-based",
    description:
      "Fire when something happens — submission, status change, review due / overdue.",
  },
  {
    value: "relative",
    title: "Individual / Relative",
    description:
      "Based on the individual's own Journey — Day 7, X days before review, no response for X days.",
  },
  {
    value: "calendar",
    title: "Fixed Calendar / Cohort",
    description:
      "Based on the programme calendar. Late joiners enter the current eligible window only.",
  },
];

export const AUTOMATION_TRIGGERS = [
  {
    id: "new_submission",
    label: "New Submission",
    description: "A person submits on this journey.",
    models: ["event"],
  },
  {
    id: "submission_updated",
    label: "Submission Updated",
    description: "An existing journey entry is updated.",
    models: ["event"],
  },
  {
    id: "review_due",
    label: "Review Due",
    description: "A scheduled review / submission checkpoint becomes due.",
    models: ["event"],
  },
  {
    id: "review_overdue",
    label: "Review Overdue",
    description: "A due review has passed without completion.",
    models: ["event"],
  },
  {
    id: "follow_up_outstanding",
    label: "Follow-up Outstanding",
    description: "A follow-up action is waiting on the person or coach.",
    models: ["event"],
  },
  {
    id: "follow_up_completed",
    label: "Follow-up Completed",
    description: "A follow-up workflow has been completed.",
    models: ["event"],
  },
  {
    id: "status_changed",
    label: "Status Changed",
    description: "A coach or admin moves the person to another status.",
    models: ["event"],
  },
  {
    id: "milestone_achieved",
    label: "Milestone Achieved",
    description: "A measurable or journey milestone is reached.",
    models: ["event"],
  },
  {
    id: "file_uploaded",
    label: "File Uploaded",
    description: "A file is uploaded on an entry.",
    models: ["event"],
  },
  {
    id: "image_uploaded",
    label: "Image Uploaded",
    description: "An image is uploaded on an entry.",
    models: ["event"],
  },
  {
    id: "relative_offset",
    label: "Relative checkpoint",
    description:
      "N days / weeks / months after journey start, last submit, or last review.",
    models: ["relative"],
  },
  {
    id: "calendar_date",
    label: "Calendar date",
    description:
      "Fixed month/day each year. Mid-cycle joiners enter the next scheduled cycle.",
    models: ["calendar"],
  },
];

export const AUTOMATION_ACTIONS = [
  {
    value: "email",
    label: "Send email",
    description: "GymRite writes and sends the message.",
  },
  {
    value: "record_event",
    label: "Record the event only",
    description:
      "No mail from GymRite. The event still exists if Power Automate is plugged in later.",
  },
  {
    value: "notify",
    label: "In-app notification",
    description: "Ping in the portal.",
  },
  {
    value: "create_coach_review",
    label: "Create coach review",
    description: "Queue a coach review for the entry.",
  },
  {
    value: "assign_status",
    label: "Assign status",
    description: "Move the person to a status on this tracker.",
  },
];

export const AUTOMATION_RECIPIENTS = [
  { value: "person", label: "Person / Member" },
  { value: "coach", label: "Assigned coach" },
  { value: "both", label: "Person and coach" },
  { value: "admins", label: "Admins" },
];

export const RELATIVE_FROM_OPTIONS = [
  { value: "journey_start", label: "Journey start" },
  { value: "last_submission", label: "Last member submission" },
  { value: "last_coach_review", label: "Last coach review" },
];

export const RELATIVE_UNITS = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

/**
 * Standard dynamic content from GymRite docs (Email Template Builder).
 * Custom Journey Responses are appended per tracker.
 */
export const STANDARD_DYNAMIC_FIELDS = [
  { token: "first_name", label: "First Name", group: "Standard" },
  { token: "full_name", label: "Full Name", group: "Standard" },
  { token: "email", label: "Email", group: "Standard" },
  { token: "coach_name", label: "Coach", group: "Standard" },
  { token: "location", label: "Location", group: "Standard" },
  { token: "journey_name", label: "Journey Name", group: "Standard" },
  { token: "start_date", label: "Start Date", group: "Standard" },
  {
    token: "current_week_stage",
    label: "Current Week/Stage",
    group: "Standard",
  },
  { token: "status", label: "Status", group: "Standard" },
  { token: "review_date", label: "Review Date", group: "Standard" },
  { token: "follow_up_date", label: "Follow-up Date", group: "Standard" },
];

const now = () => new Date().toISOString();

const slugToken = (label, fallback) => {
  const base = String(label || fallback || "field")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
  return base || fallback || "field";
};

export const toDynamicToken = (token) => `{{${token}}}`;

const LEGACY_TRIGGER_IDS = {
  on_first_submission: "new_submission",
  on_follow_up_submission: "submission_updated",
  member_review_due: "review_due",
  member_review_overdue: "review_overdue",
  coach_review_due: "review_due",
  coach_review_overdue: "review_overdue",
  journey_completed: "milestone_achieved",
  unassigned_coach: "follow_up_outstanding",
};

export const normalizeAutomationTriggerId = (id) =>
  LEGACY_TRIGGER_IDS[id] || id;

export const createEmptyWorkflow = () => ({
  id: `wf_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  description: "",
  status: "active",
  triggerModel: "event",
  triggerId: "new_submission",
  relativeAmount: 7,
  relativeUnit: "days",
  relativeFrom: "journey_start",
  calendarMonth: 1,
  calendarDay: 1,
  action: "email",
  recipients: "both",
  subject: "Update on your journey",
  message:
    "Hi {{first_name}},\n\nThis is a reminder about {{journey_name}}.\n\nThanks,\n{{coach_name}}",
  assignStatusName: "",
  createdAt: now(),
  updatedAt: now(),
});

export const createEmptyAutomation = (tracker = null) => ({
  id: `automation_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  trackerId: tracker?.id || "",
  trackerName: tracker?.name || "",
  name: tracker?.name
    ? `${tracker.name} Automation`
    : "Journey Automation",
  description: "",
  enabled: true,
  status: "active",
  recipients: "both",
  notifyUnassignedCoach: true,
  events: createTrackerAutomationSettings().events,
  workflows: [],
  createdAt: now(),
  updatedAt: now(),
});

/** @deprecated use createEmptyWorkflow — kept for older imports */
export const createEmptyAutomationWorkflow = createEmptyWorkflow;

export const getTriggerMeta = (triggerId) =>
  AUTOMATION_TRIGGERS.find((item) => item.id === triggerId) || null;

export const getTriggerLabel = (triggerId) =>
  getTriggerMeta(triggerId)?.label || triggerId || "—";

export const getTriggerModelLabel = (value) =>
  AUTOMATION_TRIGGER_MODELS.find((item) => item.value === value)?.title ||
  value ||
  "—";

export const getActionLabel = (value) =>
  AUTOMATION_ACTIONS.find((item) => item.value === value)?.label || value || "—";

export const getRecipientLabel = (value) =>
  AUTOMATION_RECIPIENTS.find((item) => item.value === value)?.label ||
  value ||
  "—";

export const getTriggersForModel = (model) =>
  AUTOMATION_TRIGGERS.filter((item) => item.models.includes(model));

export const describeWorkflowTiming = (workflow) => {
  if (!workflow) return "—";
  if (workflow.triggerModel === "relative") {
    const from =
      RELATIVE_FROM_OPTIONS.find((o) => o.value === workflow.relativeFrom)
        ?.label || "start";
    return `${workflow.relativeAmount || 0} ${
      workflow.relativeUnit || "days"
    } after ${from}`;
  }
  if (workflow.triggerModel === "calendar") {
    const month =
      MONTH_OPTIONS.find((o) => o.value === Number(workflow.calendarMonth))
        ?.label || "—";
    return `Every ${month} ${workflow.calendarDay || 1}`;
  }
  return getTriggerLabel(workflow.triggerId);
};

/** @deprecated alias */
export const describeAutomationTiming = describeWorkflowTiming;

export const normalizeWorkflow = (workflow = {}) => {
  const base = { ...createEmptyWorkflow(), ...workflow };
  const triggerId = normalizeAutomationTriggerId(
    base.triggerId || "new_submission"
  );
  const known = AUTOMATION_TRIGGERS.some((item) => item.id === triggerId);
  return {
    ...base,
    id: base.id || createEmptyWorkflow().id,
    triggerId: known ? triggerId : "new_submission",
    name: base.name || "",
    status: base.status === "draft" ? "draft" : "active",
  };
};

export const normalizeAutomation = (automation = {}) => {
  const id = String(automation.id || automation._id || "");
  const trackerId = String(automation.trackerId || "");
  const tracker = trackerId ? getTrackerById(trackerId) : null;
  const defaults = createEmptyAutomation(tracker);
  return {
    ...defaults,
    ...automation,
    id: id || defaults.id,
    trackerId,
    trackerName:
      tracker?.name || automation.trackerName || defaults.trackerName,
    name: automation.name || defaults.name,
    enabled: automation.enabled !== false,
    status: automation.status === "draft" ? "draft" : "active",
    recipients: automation.recipients || "both",
    notifyUnassignedCoach: automation.notifyUnassignedCoach !== false,
    events: normalizeAutomationEvents(automation.events),
    workflows: Array.isArray(automation.workflows)
      ? automation.workflows.map(normalizeWorkflow)
      : [],
  };
};

/** Payload for create/update API (no client-only fields). */
export const toAutomationApiPayload = (automation = {}) => {
  const shaped = normalizeAutomation(automation);
  return {
    trackerId: String(shaped.trackerId || ""),
    trackerName: shaped.trackerName || "",
    name: String(shaped.name || "").trim(),
    description: shaped.description || "",
    enabled: shaped.enabled !== false,
    status: shaped.enabled !== false ? "active" : "draft",
    recipients: shaped.recipients || "both",
    notifyUnassignedCoach: shaped.notifyUnassignedCoach !== false,
    events: shaped.events || [],
    workflows: (shaped.workflows || []).map((wf) => ({
      ...wf,
      id: wf.id,
      name: wf.name || "",
      description: wf.description || "",
      status: wf.status === "draft" ? "draft" : "active",
      triggerModel: wf.triggerModel || "event",
      triggerId: wf.triggerId || "new_submission",
      relativeAmount: Number(wf.relativeAmount) || 0,
      relativeUnit: wf.relativeUnit || "days",
      relativeFrom: wf.relativeFrom || "journey_start",
      calendarMonth: Number(wf.calendarMonth) || 1,
      calendarDay: Number(wf.calendarDay) || 1,
      action: wf.action || "email",
      recipients: wf.recipients || "both",
      subject: wf.subject || "",
      message: wf.message || "",
      assignStatusName: wf.assignStatusName || "",
      createdAt: wf.createdAt || now(),
      updatedAt: wf.updatedAt || now(),
    })),
  };
};

/**
 * Dynamic content for a tracker's workflows.
 * Standard fields + every custom question/field on the tracker (Journey Responses).
 */
export const getDynamicFieldsForTracker = (trackerId) => {
  const standard = STANDARD_DYNAMIC_FIELDS.map((item) => ({ ...item }));
  if (!trackerId) return standard;

  const tracker = getTrackerById(trackerId);
  if (!tracker) return standard;

  const library = getQuestions();
  const byId = new Map(library.map((q) => [q.id, q]));
  const seen = new Set(standard.map((f) => f.token));
  const journeyResponses = [];

  const pushQuestion = (questionLike) => {
    if (!questionLike) return;
    const lib = questionLike.questionId
      ? byId.get(questionLike.questionId)
      : null;
    const label =
      questionLike.label ||
      lib?.label ||
      questionLike.questionId ||
      "Untitled";
    let token = slugToken(label, questionLike.questionId || questionLike.id);
    if (seen.has(token)) {
      token = `${token}_${String(questionLike.id || questionLike.questionId || "")
        .slice(-6)
        .replace(/[^a-z0-9]/gi, "")}`;
    }
    if (seen.has(token)) return;
    seen.add(token);
    journeyResponses.push({
      token,
      label,
      group: "Journey Responses",
      questionId: questionLike.questionId || questionLike.id || "",
    });
  };

  (tracker.personalInfoQuestions || []).forEach(pushQuestion);
  (tracker.sharedQuestions || []).forEach(pushQuestion);
  (tracker.forms || []).forEach((form) => {
    (form.questions || []).forEach(pushQuestion);
  });
  (tracker.questions || []).forEach(pushQuestion);
  (tracker.fields || []).forEach((field) => {
    pushQuestion({
      id: field.id,
      label: field.label || field.name,
      questionId: field.id,
    });
  });

  return [...standard, ...journeyResponses];
};

export const groupDynamicFields = (fields) => {
  const groups = {};
  (fields || []).forEach((field) => {
    const group = field.group || "Other";
    if (!groups[group]) groups[group] = [];
    groups[group].push(field);
  });
  return groups;
};

const syncTrackerLink = (automation) => {
  if (!automation?.trackerId) return;
  const tracker = getTrackerById(automation.trackerId);
  if (!tracker) return;

  const defaults = createTrackerAutomationSettings();
  saveTracker({
    ...tracker,
    automation: {
      ...defaults,
      ...(tracker.automation || {}),
      automationId: automation.id,
      enabled: automation.enabled !== false,
      recipients: automation.recipients || defaults.recipients,
      notifyUnassignedCoach: automation.notifyUnassignedCoach !== false,
      events: normalizeAutomationEvents(automation.events),
      workflowIds: (automation.workflows || [])
        .filter((wf) => wf.status === "active")
        .map((wf) => wf.id),
    },
  });
};

const clearTrackerLink = (trackerId, automationId) => {
  if (!trackerId) return;
  const tracker = getTrackerById(trackerId);
  if (!tracker) return;
  if (
    tracker.automation?.automationId &&
    automationId &&
    tracker.automation.automationId !== automationId
  ) {
    return;
  }
  const defaults = createTrackerAutomationSettings();
  saveTracker({
    ...tracker,
    automation: {
      ...defaults,
      automationId: "",
      enabled: false,
      workflowIds: [],
    },
  });
};

const legacyToWorkflow = (item) =>
  normalizeWorkflow({
    id: item.id?.startsWith("wf_") ? item.id : `wf_${item.id || Date.now()}`,
    name: item.name,
    description: item.description,
    status: item.status,
    triggerModel: item.triggerModel,
    triggerId: item.triggerId,
    relativeAmount: item.relativeAmount,
    relativeUnit: item.relativeUnit,
    relativeFrom: item.relativeFrom,
    calendarMonth: item.calendarMonth,
    calendarDay: item.calendarDay,
    action: item.action,
    recipients: item.recipients,
    subject: item.subject,
    message: item.message,
    assignStatusName: item.assignStatusName,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  });

const migrateLegacyFlatAutomations = () => {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return [];

    // Already parent-shaped leftovers
    if (parsed.some((item) => Array.isArray(item?.workflows))) {
      return parsed
        .filter((item) => item?.trackerId)
        .map((item) => normalizeAutomation(item));
    }

    const trackers = getTrackers();
    const usedWorkflowIds = new Set();
    const parents = [];

    trackers.forEach((tracker) => {
      const ids = tracker.automation?.workflowIds || [];
      const workflows = ids
        .map((id) => parsed.find((item) => item.id === id))
        .filter(Boolean)
        .map((item) => {
          usedWorkflowIds.add(item.id);
          return legacyToWorkflow(item);
        });
      if (!workflows.length) return;
      const parent = createEmptyAutomation(tracker);
      parent.workflows = workflows;
      parent.enabled = tracker.automation?.enabled === true;
      parent.events = normalizeAutomationEvents(tracker.automation?.events);
      parents.push(normalizeAutomation(parent));
    });

    const orphans = parsed
      .filter((item) => !usedWorkflowIds.has(item.id))
      .map(legacyToWorkflow);

    if (orphans.length) {
      const freeTracker = trackers.find(
        (t) => !parents.some((p) => p.trackerId === t.id)
      );
      if (freeTracker) {
        const parent = createEmptyAutomation(freeTracker);
        parent.workflows = orphans;
        parents.push(normalizeAutomation(parent));
      }
    }

    return parents;
  } catch {
    return [];
  }
};

const persist = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  return list;
};

export const getAutomations = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => normalizeAutomation(item));
      }
    }
    const migrated = migrateLegacyFlatAutomations();
    if (migrated.length) {
      migrated.forEach(syncTrackerLink);
      return persist(migrated);
    }
    return persist([]);
  } catch {
    return [];
  }
};

export const getAutomationById = (id) =>
  getAutomations().find((item) => item.id === id) || null;

export const getAutomationByTrackerId = (trackerId) =>
  getAutomations().find((item) => item.trackerId === trackerId) || null;

export const getAutomationsByTrackerId = (trackerId) =>
  getAutomations().filter((item) => item.trackerId === trackerId);

export const getTrackersWithoutAutomation = () => {
  const taken = new Set(getAutomations().map((a) => a.trackerId).filter(Boolean));
  return getTrackers().filter((t) => t.id && !taken.has(t.id));
};

export const saveAutomation = (automation) => {
  const list = getAutomations();
  const payload = normalizeAutomation({
    ...automation,
    updatedAt: now(),
  });

  if (!payload.trackerId) {
    throw new Error("Automation requires a tracker");
  }

  const index = list.findIndex((item) => item.id === payload.id);
  if (index >= 0) {
    list[index] = { ...list[index], ...payload };
  } else {
    list.unshift(payload);
  }
  persist(list);
  syncTrackerLink(payload);
  return payload;
};

export const saveWorkflowOnAutomation = (automationId, workflow) => {
  const parent = getAutomationById(automationId);
  if (!parent) throw new Error("Automation not found");
  const nextWorkflow = normalizeWorkflow({
    ...workflow,
    updatedAt: now(),
  });
  const workflows = [...(parent.workflows || [])];
  const index = workflows.findIndex((item) => item.id === nextWorkflow.id);
  if (index >= 0) workflows[index] = { ...workflows[index], ...nextWorkflow };
  else workflows.unshift(nextWorkflow);
  return saveAutomation({ ...parent, workflows });
};

export const deleteWorkflowFromAutomation = (automationId, workflowId) => {
  const parent = getAutomationById(automationId);
  if (!parent) return null;
  return saveAutomation({
    ...parent,
    workflows: (parent.workflows || []).filter((item) => item.id !== workflowId),
  });
};

export const deleteAutomation = (id) => {
  const existing = getAutomationById(id);
  const list = getAutomations().filter((item) => item.id !== id);
  persist(list);
  if (existing?.trackerId) {
    clearTrackerLink(existing.trackerId, id);
  }
  return list;
};

/** Flat list — one row per workflow (GymRite Automations list). */
export const getAutomationRules = () => {
  const rules = [];
  getAutomations().forEach((parent) => {
    (parent.workflows || []).forEach((wf) => {
      rules.push({
        ...wf,
        parentId: parent.id,
        trackerId: parent.trackerId,
        trackerName: parent.trackerName,
        parentEnabled: parent.enabled !== false,
        parentName: parent.name,
      });
    });
  });
  return rules.sort((a, b) =>
    String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))
  );
};

export const getAutomationRuleById = (workflowId) =>
  getAutomationRules().find((item) => item.id === workflowId) || null;

/**
 * Create or update a single When/Who/Then/Write rule.
 * Ensures a parent automation exists for the tracker (1 parent : many rules).
 */
export const saveAutomationRule = (rule = {}) => {
  const trackerId = rule.trackerId;
  if (!trackerId) throw new Error("Pick a tracker for this automation");

  let parent = rule.parentId
    ? getAutomationById(rule.parentId)
    : getAutomationByTrackerId(trackerId);

  if (parent && parent.trackerId && parent.trackerId !== trackerId) {
    throw new Error("This rule belongs to another tracker");
  }

  if (!parent) {
    const tracker = getTrackerById(trackerId);
    if (!tracker) throw new Error("Tracker not found");
    parent = saveAutomation({
      ...createEmptyAutomation(tracker),
      workflows: [],
    });
  }

  const workflowId = rule.id || createEmptyWorkflow().id;
  const savedParent = saveWorkflowOnAutomation(parent.id, {
    ...rule,
    id: workflowId,
  });
  const workflow =
    (savedParent.workflows || []).find((item) => item.id === workflowId) ||
    null;

  if (!workflow) {
    throw new Error("Could not save automation");
  }

  return {
    ...workflow,
    parentId: savedParent.id,
    trackerId: savedParent.trackerId,
    trackerName: savedParent.trackerName,
    parentEnabled: savedParent.enabled !== false,
    parentName: savedParent.name,
  };
};

export const deleteAutomationRule = (workflowId) => {
  const rule = getAutomationRuleById(workflowId);
  if (!rule?.parentId) return null;
  const parent = deleteWorkflowFromAutomation(rule.parentId, workflowId);
  if (parent && !(parent.workflows || []).length) {
    deleteAutomation(parent.id);
    return null;
  }
  return parent;
};

export const resetAutomationsToDefaults = () => {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
  return persist([]);
};

/**
 * Apply enable / event / recipient patches from the tracker builder drawer
 * onto the parent automation (source of truth for workflows).
 */
export const applyTrackerAutomationSettings = (trackerId, settings = {}) => {
  let parent = getAutomationByTrackerId(trackerId);
  if (!parent) {
    const tracker = getTrackerById(trackerId);
    if (!tracker) return null;
    parent = createEmptyAutomation(tracker);
  }
  return saveAutomation({
    ...parent,
    enabled: settings.enabled !== false,
    recipients: settings.recipients || parent.recipients,
    notifyUnassignedCoach:
      settings.notifyUnassignedCoach !== undefined
        ? settings.notifyUnassignedCoach
        : parent.notifyUnassignedCoach,
    events: normalizeAutomationEvents(settings.events || parent.events),
  });
};

// ——— API-backed helpers (preferred) ———

const looksLikeMongoId = (id) =>
  Boolean(id) && /^[a-f\d]{24}$/i.test(String(id));

export const loadAutomationsFromApi = async (trackerId = "") => {
  try {
    const res = await getTrackerAutomationsApi({
      limit: 500,
      trackerId: trackerId || "",
    });
    if (res?.code === 200 && Array.isArray(res.automations)) {
      const list = res.automations.map(normalizeAutomation);
      persist(list);
      return { ok: true, automations: list, fromApi: true };
    }
    return {
      ok: false,
      automations: getAutomations(),
      fromApi: false,
      message: res?.message || "Failed to load automations",
    };
  } catch (err) {
    return {
      ok: false,
      automations: getAutomations(),
      fromApi: false,
      message: err?.message || "Failed to load automations",
    };
  }
};

export const loadAutomationByIdFromApi = async (id) => {
  if (!id) return { ok: false, automation: null };
  try {
    if (looksLikeMongoId(id)) {
      const res = await getTrackerAutomationDetailApi(id);
      if (res?.code === 200 && res.automation) {
        const shaped = normalizeAutomation(res.automation);
        const list = getAutomations();
        const idx = list.findIndex((item) => item.id === shaped.id);
        if (idx >= 0) list[idx] = shaped;
        else list.unshift(shaped);
        persist(list);
        return { ok: true, automation: shaped, fromApi: true };
      }
    }
    const local = getAutomationById(id);
    return {
      ok: Boolean(local),
      automation: local,
      fromApi: false,
      message: local ? "" : "Automation not found",
    };
  } catch (err) {
    const local = getAutomationById(id);
    return {
      ok: Boolean(local),
      automation: local,
      fromApi: false,
      message: err?.message || "Failed to load automation",
    };
  }
};

export const saveAutomationViaApi = async (automation) => {
  const payload = toAutomationApiPayload(automation);
  if (!payload.trackerId) {
    throw new Error("Automation requires a tracker");
  }

  const existingId = String(automation?.id || automation?._id || "");
  const isUpdate = looksLikeMongoId(existingId);

  const res = isUpdate
    ? await updateTrackerAutomationApi(existingId, payload)
    : await addTrackerAutomationApi(payload);

  if (res?.code !== 200 || !res.automation) {
    throw new Error(res?.message || "Could not save automation");
  }

  const shaped = normalizeAutomation(res.automation);
  const list = getAutomations();
  const idx = list.findIndex((item) => item.id === shaped.id);
  if (idx >= 0) list[idx] = shaped;
  else list.unshift(shaped);
  persist(list);
  syncTrackerLink(shaped);
  return shaped;
};

export const deleteAutomationViaApi = async (id) => {
  if (!id) return false;
  if (looksLikeMongoId(id)) {
    const res = await deleteTrackerAutomationApi(id);
    if (res?.code !== 200) {
      throw new Error(res?.message || "Could not delete automation");
    }
  }
  deleteAutomation(id);
  return true;
};

export const saveWorkflowOnAutomationViaApi = async (
  automationId,
  workflow
) => {
  const parentResult = await loadAutomationByIdFromApi(automationId);
  const parent = parentResult.automation;
  if (!parent) throw new Error("Automation not found");

  const nextWorkflow = normalizeWorkflow({
    ...workflow,
    updatedAt: now(),
  });
  const workflows = [...(parent.workflows || [])];
  const index = workflows.findIndex((item) => item.id === nextWorkflow.id);
  if (index >= 0) workflows[index] = { ...workflows[index], ...nextWorkflow };
  else workflows.unshift(nextWorkflow);

  return saveAutomationViaApi({ ...parent, workflows });
};

export const deleteWorkflowFromAutomationViaApi = async (
  automationId,
  workflowId
) => {
  const parentResult = await loadAutomationByIdFromApi(automationId);
  const parent = parentResult.automation;
  if (!parent) return null;
  return saveAutomationViaApi({
    ...parent,
    workflows: (parent.workflows || []).filter(
      (item) => item.id !== workflowId
    ),
  });
};

export const applyTrackerAutomationSettingsViaApi = async (
  trackerId,
  settings = {}
) => {
  const loaded = await loadAutomationsFromApi(trackerId);
  let parent =
    (loaded.automations || []).find((a) => a.trackerId === trackerId) || null;
  if (!parent) {
    const tracker = getTrackerById(trackerId);
    if (!tracker) return null;
    parent = createEmptyAutomation(tracker);
  }
  return saveAutomationViaApi({
    ...parent,
    enabled: settings.enabled !== false,
    recipients: settings.recipients || parent.recipients,
    notifyUnassignedCoach:
      settings.notifyUnassignedCoach !== undefined
        ? settings.notifyUnassignedCoach
        : parent.notifyUnassignedCoach,
    events: normalizeAutomationEvents(settings.events || parent.events),
  });
};
